/**
 * MODULE: _shared/frontend/lib/frontend/hooks/useTenantDefaultCurrency.ts
 * PURPOSE: Load tenant default_currency from tenant-service for display formatting
 * DEPENDENCIES: @tanstack/react-query, @core/tenant, @shared/lib/frontend/api/config, formatCurrency utils
 * PUBLIC_API: useTenantDefaultCurrency, TENANT_CURRENCY_QUERY_KEY
 * SIDE_EFFECTS: HTTP GET /api/v1/tenants/my-tenants (avoids 403 on GET /tenants/:id when localStorage drifted)
 * RECENT: 2026-06-12 — Load via my-tenants + JWT-aligned tenant id (gateway tenant isolation).
 * AGENT_SAFETY: SAFE
 */

import { useQuery } from '@tanstack/react-query';
import { tenantApi } from '@core/tenant';
import { getJwtTenantId, getTenantId } from '@shared/lib/frontend/api/config';
import {
  DEFAULT_CRM_CURRENCY,
  DEFAULT_CRM_LOCALE,
  formatCurrency,
  localeForCurrency,
} from '@shared/lib/frontend/utils/formatCurrency';

export const TENANT_CURRENCY_QUERY_KEY = ['tenant', 'default_currency'] as const;

const SUPPORTED_CURRENCIES = ['GBP', 'USD', 'EUR'] as const;
export type TenantDisplayCurrency = (typeof SUPPORTED_CURRENCIES)[number];

export function isTenantDisplayCurrency(code: string): code is TenantDisplayCurrency {
  return (SUPPORTED_CURRENCIES as readonly string[]).includes(code.toUpperCase());
}

function normalizeCurrency(raw: string | undefined | null): TenantDisplayCurrency {
  const upper = (raw ?? DEFAULT_CRM_CURRENCY).toUpperCase();
  return isTenantDisplayCurrency(upper) ? upper : DEFAULT_CRM_CURRENCY;
}

/** Path param for tenant-service (expects tnt_ prefix; localStorage may store bare UUID). */
export function tenantIdForTenantApi(id: string): string {
  const t = id.trim();
  if (!t) return t;
  return t.startsWith('tnt_') ? t : `tnt_${t}`;
}

function bareTenantUuid(id: string): string {
  return id.trim().replace(/^tnt_/, '');
}

/** Resolve active tenant id: JWT claim wins (matches API gateway enforcement). */
export function resolveActiveTenantId(fallback?: string | null): string {
  return getJwtTenantId() ?? fallback ?? getTenantId() ?? '';
}

export function useTenantDefaultCurrency(tenantIdOverride?: string | null) {
  const tenantId = resolveActiveTenantId(tenantIdOverride);

  const query = useQuery({
    queryKey: [...TENANT_CURRENCY_QUERY_KEY, tenantId],
    queryFn: async () => {
      if (!tenantId) return { currency: DEFAULT_CRM_CURRENCY as TenantDisplayCurrency };
      const targetBare = bareTenantUuid(tenantId);
      try {
        const tenants = await tenantApi.list();
        const match = tenants.find((t) => bareTenantUuid(t.id) === targetBare);
        if (match?.default_currency) {
          return { currency: normalizeCurrency(match.default_currency) };
        }
        const tenant = await tenantApi.get(tenantIdForTenantApi(tenantId));
        const currency = normalizeCurrency(
          (tenant as { default_currency?: string }).default_currency
        );
        return { currency };
      } catch {
        return { currency: DEFAULT_CRM_CURRENCY as TenantDisplayCurrency };
      }
    },
    enabled: Boolean(tenantId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const currency = query.data?.currency ?? DEFAULT_CRM_CURRENCY;
  const locale = localeForCurrency(currency);

  return {
    tenantId,
    currency,
    locale,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    formatCurrency: (amount: number) => formatCurrency(amount, { currency, locale }),
    supportedCurrencies: SUPPORTED_CURRENCIES,
  };
}

export { DEFAULT_CRM_CURRENCY, DEFAULT_CRM_LOCALE };
